import { useContext, useEffect, useState } from 'react';
import {
  ShowOptions,
  User,
  UserParameters,
  UserShowOption,
  UserUpdate,
} from '../model/models';
import { useLogger } from '../services/LogService';
import { ModelsService } from '../services/ModelsService';
import { AppContext } from '../state/AppContextProvider';
import { UpdateUserFunc } from './WPHooks';

export type UpdateParameterFunction = (
  name: string,
  value: string | Array<number> | boolean | ShowOptions | number | undefined
) => void;

export const useParameters = (
  user: User | undefined,
  update: UpdateUserFunc
): { updateParameter: UpdateParameterFunction; parameters: UserParameters } => {
  const logger = useLogger('useParameters');

  const [triggerSave, setTriggerSave] = useState(0);

  const [saveTask, setSaveTask] = useState<any>();

  const { state } = useContext(AppContext);

  const initParameters = () => {
    const userParentTags = (user?.acf.tags || [])
      .map((t) => ({
        id: t.term_id,
        parent: ModelsService.getParentTag(t.parent, state.parentTags),
      }))
      .filter((p) => p.parent !== undefined);

    const newParameters = {
      locale: user?.acf.locale || '',
      pushNotification: false,
      notifications: user?.acf.notifications === true ? true : false,
      confidentiality: user?.acf.confidentiality || 'private',
      photo: user?.acf.photo || '',
      establishment: userParentTags.find(
        (p) => p.parent?.slug === 'establishment'
      )?.id,
      discipline: userParentTags.find((p) => p.parent?.slug === 'discipline')
        ?.id,
      studyYear: userParentTags.find((p) => p.parent?.slug === 'study_year')
        ?.id,
      interest: userParentTags
        .filter((p) => p.parent?.slug === 'interest')
        .map((p) => p.id),
      show: {
        image: user?.acf.show?.includes('image') || false,
        mobile: user?.acf.show?.includes('mobile') || false,
        email: user?.acf.show?.includes('email') || false,
        name: user?.acf.show?.includes('name') || false,
        house: user?.acf.show?.includes('house') || false,
      },
    };
    return newParameters;
  };

  const [parameters, setParameters] = useState<UserParameters>(
    initParameters()
  );

  useEffect(() => {
    if (state.parentTags.length > 0) {
      const p = initParameters();
      logger.info('useEffect(state.parentTags.length) initParameters()', p);
      setParameters(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.parentTags.length]);

  // save parameters on each change
  // we use a setTimeout to delay the update and prevent high request rate
  useEffect(() => {
    if (triggerSave > 0) {
      setTriggerSave(0);
      if (saveTask) {
        // first cancel the pending task if exists
        clearTimeout(saveTask);
      }
      // delay the save action
      setSaveTask(setTimeout(saveParameters, 1000));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSave]);

  const saveParameters = () => {
    const data: UserUpdate = {
      acf: {
        locale: parameters.locale,
        notifications: parameters.notifications,
        confidentiality: parameters.confidentiality,
        photo: parameters.photo,
        tags: [
          ...parameters.interest,
          parameters.establishment,
          parameters.discipline,
          parameters.studyYear,
        ]
          .filter((t) => t !== undefined)
          .map((t) => t?.toString()) as string[],
        show: Object.entries(parameters.show)
          .filter((it) => it[1] === true)
          .map((it) => it[0] as UserShowOption),
      },
    };
    update(data);
  };

  const updateParameter: UpdateParameterFunction = (name, value) => {
    if (name !== null && name !== undefined) {
      setParameters({ ...parameters, [name]: value });
      setTriggerSave(triggerSave + 1);
      logger.debug(`updateParameter('${name}', '${value}')`);
    } else {
      logger.info(`updateParameter() name to update is null or undefined`, {
        name: name,
        value: value,
      });
    }
  };

  return {
    updateParameter: updateParameter,
    parameters: parameters,
  };
};
